/**
 * The graph settings panel: Obsidian's Filters, Groups, Display and Forces,
 * plus the local-graph section when the scope is the right-panel graph.
 */
import { useMemo } from "react";
import { ChevronDown, ChevronRight, Plus, X } from "lucide-react";
import { Button } from "@/ui/Button";
import { IconButton } from "@/ui/IconButton";
import {
  RANGES,
  useGraph,
  type GraphScope,
  type GraphSection,
  type LocalGraphSettings,
} from "@/store/graph";
import { QueryInput, sourceFrom } from "./QueryInput";
import type { GraphData, GraphStats } from "./types";
import s from "./Graph.module.css";

interface Props {
  scope: GraphScope;
  stats: GraphStats;
  /** The UNFILTERED graph: query suggestions must offer filtered-out values. */
  full: GraphData;
  warnings: string[];
}

export function GraphSidebar({ scope, stats, full, warnings }: Props) {
  const store = useGraph();
  const settings = scope === "global" ? store.global : store.local;
  const isLocal = scope === "local";
  const source = useMemo(() => sourceFrom(full), [full]);

  return (
    <div className={s.sidebar}>
      <Section id="filters" title="Filters" scope={scope}>
        <QueryInput
          placeholder="Search files..."
          value={settings.filters.search}
          onChange={(v) => store.setFilters(scope, { search: v })}
          source={source}
          ariaLabel="Graph search"
        />
        {warnings.map((w) => (
          <div key={w} className={s.warning}>
            {w}
          </div>
        ))}
        <Toggle
          label="Tags"
          checked={settings.filters.showTags}
          onChange={(v) => store.setFilters(scope, { showTags: v })}
        />
        <Toggle
          label="Attachments"
          checked={settings.filters.showAttachments}
          onChange={(v) => store.setFilters(scope, { showAttachments: v })}
        />
        <Toggle
          label="Existing files only"
          checked={settings.filters.existingOnly}
          onChange={(v) => store.setFilters(scope, { existingOnly: v })}
        />
        <Toggle
          label="Orphans"
          checked={settings.filters.showOrphans}
          onChange={(v) => store.setFilters(scope, { showOrphans: v })}
        />
      </Section>

      <Section id="groups" title="Groups" scope={scope}>
        {settings.groups.map((g, i) => (
          <div key={g.id} className={s.group}>
            <span className={s.groupIndex} title="Priority">
              {i + 1}
            </span>
            <QueryInput
              className={s.groupQuery}
              placeholder="Query"
              value={g.query}
              onChange={(v) => store.updateGroup(scope, g.id, { query: v })}
              source={source}
              ariaLabel={`Group ${i + 1} query`}
            />
            <input
              type="color"
              className={s.swatch}
              value={g.color}
              onChange={(e) => store.updateGroup(scope, g.id, { color: e.target.value })}
              aria-label={`Group ${i + 1} color`}
            />
            <IconButton label="Remove group" onClick={() => store.removeGroup(scope, g.id)}>
              <X size={12} />
            </IconButton>
          </div>
        ))}
        <Button size="sm" variant="subtle" onClick={() => store.addGroup(scope)}>
          <Plus size={12} /> New group
        </Button>
        {settings.groups.length > 1 ? (
          <div className={s.groupHint}>The first matching group sets the color.</div>
        ) : null}
      </Section>

      <Section id="display" title="Display" scope={scope}>
        <Toggle
          label="Arrows"
          checked={settings.display.showArrows}
          onChange={(v) => store.setDisplay(scope, { showArrows: v })}
        />
        <Toggle
          label="Color by folder"
          checked={settings.display.colorByFolder}
          onChange={(v) => store.setDisplay(scope, { colorByFolder: v })}
        />
        <Slider
          label="Text fade threshold"
          range="textFadeMultiplier"
          value={settings.display.textFadeMultiplier}
          onChange={(v) => store.setDisplay(scope, { textFadeMultiplier: v })}
        />
        <Slider
          label="Node size"
          range="nodeSizeMultiplier"
          value={settings.display.nodeSizeMultiplier}
          onChange={(v) => store.setDisplay(scope, { nodeSizeMultiplier: v })}
        />
        <Slider
          label="Link thickness"
          range="lineSizeMultiplier"
          value={settings.display.lineSizeMultiplier}
          onChange={(v) => store.setDisplay(scope, { lineSizeMultiplier: v })}
        />
      </Section>

      <Section id="forces" title="Forces" scope={scope}>
        <Slider
          label="Center force"
          range="centerStrength"
          value={settings.forces.centerStrength}
          onChange={(v) => store.setForces(scope, { centerStrength: v })}
        />
        <Slider
          label="Repel force"
          range="repelStrength"
          value={settings.forces.repelStrength}
          onChange={(v) => store.setForces(scope, { repelStrength: v })}
        />
        <Slider
          label="Link force"
          range="linkStrength"
          value={settings.forces.linkStrength}
          onChange={(v) => store.setForces(scope, { linkStrength: v })}
        />
        <Slider
          label="Link distance"
          range="linkDistance"
          value={settings.forces.linkDistance}
          onChange={(v) => store.setForces(scope, { linkDistance: v })}
        />
      </Section>

      {isLocal ? (
        <Section id="local" title="Local graph" scope={scope}>
          <Slider
            label="Depth"
            range="depth"
            value={(settings as LocalGraphSettings).local.depth}
            onChange={(v) => store.setLocalOptions({ depth: v })}
          />
          <Toggle
            label="Incoming links"
            checked={(settings as LocalGraphSettings).local.incoming}
            onChange={(v) => store.setLocalOptions({ incoming: v })}
          />
          <Toggle
            label="Outgoing links"
            checked={(settings as LocalGraphSettings).local.outgoing}
            onChange={(v) => store.setLocalOptions({ outgoing: v })}
          />
          <Toggle
            label="Neighbor links"
            checked={(settings as LocalGraphSettings).local.neighborLinks}
            onChange={(v) => store.setLocalOptions({ neighborLinks: v })}
          />
        </Section>
      ) : null}

      <div className={s.stats}>
        {describeStats(stats)}
        {stats.links === 0 && stats.notes > 1 ? (
          <div className={s.statsHint}>
            Nothing is connected yet. Notes link when one references another by
            title: type [[ in a note body to add a wikilink.
          </div>
        ) : null}
      </div>

      <div className={s.actions}>
        <Button size="sm" variant="subtle" onClick={() => store.restoreDefaults(scope)}>
          Restore default settings
        </Button>
      </div>
    </div>
  );
}

function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

function describeStats(stats: GraphStats): string {
  const parts = [plural(stats.notes, "note"), plural(stats.links, "link")];
  if (stats.tags) parts.push(plural(stats.tags, "tag"));
  if (stats.attachments) parts.push(plural(stats.attachments, "attachment"));
  if (stats.unresolved) parts.push(`${stats.unresolved} unresolved`);
  if (stats.orphans) parts.push(plural(stats.orphans, "orphan"));
  if (stats.titleCollisions) parts.push(`${plural(stats.titleCollisions, "duplicate title")}`);
  return parts.join(", ");
}

function Section({
  id,
  title,
  scope,
  children,
}: {
  id: GraphSection;
  title: string;
  scope: GraphScope;
  children: React.ReactNode;
}) {
  const collapsed = useGraph((st) => st.collapsed[id]);
  const toggle = useGraph((st) => st.toggleSection);
  void scope;
  return (
    <div className={s.section}>
      <button type="button" className={s.sectionHead} onClick={() => toggle(id)} aria-expanded={!collapsed}>
        {collapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
        {title}
      </button>
      {collapsed ? null : <div className={s.sectionBody}>{children}</div>}
    </div>
  );
}

function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className={s.row}>
      <span>{label}</span>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
    </label>
  );
}

function Slider({
  label,
  range,
  value,
  onChange,
}: {
  label: string;
  range: keyof typeof RANGES;
  value: number;
  onChange: (v: number) => void;
}) {
  const [min, max, step] = RANGES[range];
  const shown = step >= 1 ? value.toFixed(0) : value.toFixed(2);
  return (
    <div className={s.slider}>
      <div className={s.sliderLabel}>
        <span>{label}</span>
        <span>{shown}</span>
      </div>
      <input
        type="range"
        className={s.sliderInput}
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        aria-label={label}
      />
    </div>
  );
}
